import { GET_LOGGED_IN_USER } from '@dai/graphql'
import * as Sentry from '@sentry/react'
import { client } from 'api/client'
import { auth, firestore, logAnalyticsEvent } from 'api/Firebase'
import {
  ActionCodeInfo,
  ActionCodeSettings,
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getRedirectResult,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  User,
  verifyBeforeUpdateEmail,
} from 'firebase/auth'
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import {
  createContext,
  ReactNode,
  useEffect,
  useReducer,
  useState,
} from 'react'

import useT from '@/i18n/useT'

interface AuthUser extends User {
  avatar?: string
}

export interface AuthContextType {
  creatingUser: boolean
  setCreatingUser: (value: boolean) => void
  isAuthenticated: boolean
  isInitialized: boolean
  user: AuthUser | null
  signIn: (email: string, password: string) => Promise<User>
  createUser: (email: string, password: string) => Promise<User>
  signOut: () => Promise<void>
  verifyEmail: (code: string) => Promise<void>
  verifyAndUpdateEmail: (code: string) => Promise<void>
  verifyAndRecoverEmail: (code: string) => Promise<void>
  sendVerifyBeforeUpdateEmail: (
    newEmail: string,
    continueUrl?: string,
  ) => Promise<void>
  sendEmailVerification: (continueUrl?: string) => Promise<void>
  sendPasswordResetEmail: (email: string, continueUrl?: string) => Promise<void>
  checkActionCode: (code: string) => Promise<ActionCodeInfo>
  confirmPasswordReset: (code: string, password: string) => Promise<void>
}

enum AuthActionType {
  INITIALIZE = 'INITIALIZE',
  SIGN_OUT = 'SIGN_OUT',
  VERIFY_EMAIL = 'VERIFY_EMAIL',
}

type AuthState = {
  isAuthenticated: boolean
  isInitialized: boolean
  user: AuthUser | null
}

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
}

const reducer = (
  state: AuthState,
  action: { type: AuthActionType; payload?: Partial<AuthState> },
) => {
  switch (action.type) {
    case AuthActionType.INITIALIZE:
      return {
        ...state,
        ...action.payload,
        isInitialized: true,
      }
    case AuthActionType.SIGN_OUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isInitialized: true,
      }
    case AuthActionType.VERIFY_EMAIL:
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
              emailVerified: true,
            }
          : state.user,
      }
    default:
      return state
  }
}

const AuthContext = createContext<AuthContextType | null>(null)

function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [creatingUser, setCreatingUser] = useState(false)
  const { t } = useT()

  useEffect(() => {
    let unsubscribe: (() => void) | undefined

    const syncAuthenticatedUser = async (user: User) => {
      Sentry.setUser({ id: user.uid, email: user.email || undefined })
      const userDoc = await getDoc(doc(firestore, 'users', user.uid))
      if (userDoc.exists() && user.email !== userDoc.data().email) {
        await updateDoc(doc(firestore, 'users', user.uid), {
          email: user.email,
        })
      }

      await client.refetchQueries({
        include: [GET_LOGGED_IN_USER],
      })

      dispatch({
        type: AuthActionType.INITIALIZE,
        payload: {
          isAuthenticated: true,
          user: userDoc.exists() ? { ...user, ...userDoc.data() } : user,
        },
      })
    }

    const initAuth = async () => {
      try {
        const redirectResult = await getRedirectResult(auth)
        if (redirectResult?.user) {
          const providerId =
            redirectResult.providerId ??
            redirectResult.user.providerData[0]?.providerId ??
            'oauth'
          const method = providerId.includes('microsoft')
            ? 'microsoft'
            : providerId.includes('password')
              ? 'email'
              : providerId
          logAnalyticsEvent('login', { method })
          await syncAuthenticatedUser(redirectResult.user)
        }
      } catch (error) {
        console.error('OAuth redirect sign-in failed', error)
      }

      unsubscribe = onAuthStateChanged(auth, async user => {
        if (user) {
          await syncAuthenticatedUser(user)
        } else {
          dispatch({
            type: AuthActionType.SIGN_OUT,
          })
        }
      })
    }

    void initAuth()

    return () => unsubscribe?.()
  }, [])

  const signIn = async (email: string, password: string): Promise<User> => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      )
      const user = userCredential.user

      if (!user?.uid) {
        throw new Error(t('firebaseAuth.error.userUidMissing'))
      }

      const userDoc = await getDoc(doc(firestore, 'users', user.uid))

      await client.refetchQueries({
        include: [GET_LOGGED_IN_USER],
      })

      dispatch({
        type: AuthActionType.INITIALIZE,
        payload: {
          isAuthenticated: true,
          user: userDoc.exists() ? { ...user, ...userDoc.data() } : user,
        },
      })

      logAnalyticsEvent('login', {
        method: 'email',
      })

      return user
    } catch (error: any) {
      const message = error.message || t('common.somethingWentWrong')
      throw new Error(message)
    }
  }

  const handleSendEmailVerification = async (continueUrl?: string) => {
    if (!auth.currentUser) {
      throw new Error(t('firebaseAuth.error.cannotSendVerification'))
    }
    const actionCodeSettings: ActionCodeSettings | null = continueUrl
      ? {
          url: continueUrl,
          handleCodeInApp: true,
        }
      : null
    await sendEmailVerification(auth.currentUser, actionCodeSettings)
  }

  const handleSendVerifyBeforeUpdateEmail = async (
    newEmail: string,
    continueUrl?: string,
  ) => {
    if (!auth.currentUser) {
      throw new Error(t('firebaseAuth.error.cannotUpdateEmail'))
    }

    const actionCodeSettings: ActionCodeSettings | null = continueUrl
      ? {
          url: continueUrl,
          handleCodeInApp: true,
        }
      : null
    await verifyBeforeUpdateEmail(
      auth.currentUser,
      newEmail,
      actionCodeSettings,
    )
  }

  const handleSignOut = async () => {
    await signOut(auth)
    await client.resetStore()
    localStorage.clear()
    dispatch({
      type: AuthActionType.SIGN_OUT,
    })
  }

  const createUser = async (email: string, password: string): Promise<User> => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      )
      const user = userCredential.user

      if (!user?.uid) {
        console.error(t('firebaseAuth.error.signupFailed'))
        throw new Error(t('firebaseAuth.error.userUidMissing'))
      }

      if (!import.meta.env.VITE_APP__TENANT_NAME) {
        throw new Error('Missing Tenant information')
      }

      const tenantRef = doc(
        firestore,
        'tenants',
        import.meta.env.VITE_APP__TENANT_NAME,
      )

      await setDoc(doc(firestore, 'users', email), {
        uid: user.uid,
        email,
        tenant: tenantRef,
      })

      dispatch({
        type: AuthActionType.INITIALIZE,
        payload: { isAuthenticated: true, user },
      })

      return user
    } catch (error: any) {
      const message = error.message || t('common.somethingWentWrong')
      throw new Error(message)
    }
  }

  const handleSendPasswordResetEmail = async (
    email: string,
    continueUrl?: string,
  ) => {
    const actionCodeSettings: ActionCodeSettings | undefined = continueUrl
      ? {
          url: continueUrl,
          handleCodeInApp: true,
        }
      : undefined
    await sendPasswordResetEmail(auth, email, actionCodeSettings)
  }

  const verifyEmail = async (code: string) => {
    await applyActionCode(auth, code)
    dispatch({
      type: AuthActionType.VERIFY_EMAIL,
    })
  }

  const verifyAndUpdateEmail = async (code: string) => {
    await applyActionCode(auth, code)
    dispatch({
      type: AuthActionType.SIGN_OUT,
    })
  }

  const verifyAndRecoverEmail = async (code: string) => {
    await applyActionCode(auth, code)
    dispatch({
      type: AuthActionType.SIGN_OUT,
    })
  }

  const handleCheckActionCode = async (code: string) => {
    return await checkActionCode(auth, code)
  }

  const handleConfirmPasswordReset = async (code: string, password: string) => {
    await confirmPasswordReset(auth, code, password)
  }

  if (!state.isInitialized) return null

  return (
    <AuthContext.Provider
      value={{
        ...state,
        creatingUser,
        setCreatingUser,
        signIn,
        createUser,
        signOut: handleSignOut,
        sendVerifyBeforeUpdateEmail: handleSendVerifyBeforeUpdateEmail,
        sendEmailVerification: handleSendEmailVerification,
        sendPasswordResetEmail: handleSendPasswordResetEmail,
        verifyEmail,
        verifyAndUpdateEmail,
        verifyAndRecoverEmail,
        checkActionCode: handleCheckActionCode,
        confirmPasswordReset: handleConfirmPasswordReset,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthContext, AuthProvider }
